import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Banknote } from 'lucide-react';
import GuestSelector from '../GuestSelector';
import { toNumber, safeFixed } from '../../utils/formatters';

export default function ReservationFormDialog({
  open,
  onOpenChange,
  selectedReservation,
  formData,
  setFormData,
  channels,
  guests,
  isNewGuest,
  setIsNewGuest,
  newGuestData,
  setNewGuestData,
  onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {selectedReservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
          </DialogTitle>
        </DialogHeader>
        <form id="reservation-form" onSubmit={onSubmit} className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Client</Label>
                {!selectedReservation && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setIsNewGuest(!isNewGuest); setFormData({...formData, guest_id: ''}); }}
                    className="text-xs"
                  >
                    {isNewGuest ? '← Choisir client existant' : '+ Nouveau client'}
                  </Button>
                )}
              </div>
              
              {isNewGuest ? (
                <div className="p-3 border rounded-lg bg-blue-50 space-y-3">
                  <p className="text-sm font-medium text-blue-800">Nouveau client</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Prénom *</Label>
                      <Input
                        value={newGuestData.first_name}
                        onChange={(e) => setNewGuestData({ ...newGuestData, first_name: e.target.value })}
                        placeholder="Prénom"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nom *</Label>
                      <Input
                        value={newGuestData.last_name}
                        onChange={(e) => setNewGuestData({ ...newGuestData, last_name: e.target.value })}
                        placeholder="Nom"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        value={newGuestData.email}
                        onChange={(e) => setNewGuestData({ ...newGuestData, email: e.target.value })}
                        placeholder="email@exemple.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Téléphone</Label>
                      <Input
                        value={newGuestData.phone}
                        onChange={(e) => setNewGuestData({ ...newGuestData, phone: e.target.value })}
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <GuestSelector
                  guests={guests}
                  value={formData.guest_id}
                  onChange={(v) => setFormData({ ...formData, guest_id: v })}
                  placeholder="Rechercher un client..."
                />
              )}
            </div>
            <div>
              <Label>Source</Label>
              <Select 
                value={formData.source} 
                onValueChange={(v) => {
                  const channel = channels.find(c => c.code === v);
                  setFormData({ 
                    ...formData, 
                    source: v,
                    commission_rate: channel?.metadata?.commission_rate || 0
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {channels.map(channel => (
                    <SelectItem key={channel.code} value={channel.code}>
                      <span className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: channel.color }}
                        />
                        {channel.label}
                        {channel.metadata?.commission_rate > 0 && (
                          <span className="text-xs text-muted-foreground">
                            ({channel.metadata.commission_rate}%)
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Référence plateforme</Label>
              <Input
                value={formData.platform_ref}
                onChange={(e) => setFormData({ ...formData, platform_ref: e.target.value })}
                placeholder="Ex: BK123456"
              />
            </div>
            <div>
              <Label>Date d'arrivée</Label>
              <Input
                type="date"
                value={formData.check_in}
                onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Date de départ</Label>
              <Input
                type="date"
                value={formData.check_out}
                onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Adultes</Label>
              <Input
                type="number"
                min="1"
                value={formData.adults}
                onChange={(e) => setFormData({ ...formData, adults: parseInt(e.target.value) })}
                required
              />
            </div>
            <div>
              <Label>Enfants</Label>
              <Input
                type="number"
                min="0"
                value={formData.children}
                onChange={(e) => setFormData({ ...formData, children: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Montant total</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Commission (montant)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.commission_amount || ''}
                onChange={(e) => {
                  const amount = parseFloat(e.target.value) || 0;
                  const rate = formData.total_amount > 0 ? (amount / formData.total_amount) * 100 : 0;
                  setFormData({ ...formData, commission_amount: amount, commission_rate: rate });
                }}
                placeholder="Ex: 675"
              />
              {toNumber(formData.total_amount) > 0 && toNumber(formData.commission_amount) > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  = {safeFixed((toNumber(formData.commission_amount) / toNumber(formData.total_amount)) * 100, 1)}% du total
                </p>
              )}
            </div>
            <div>
              <Label>Devise</Label>
              <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MAD">MAD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Info Paiements - uniquement pour réservations directes */}
            {formData.source === 'DIRECT' && selectedReservation && (
              <div className="col-span-2 border rounded-lg p-4 bg-blue-50/50 border-blue-200">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Les paiements (acomptes et solde) se gèrent via l'onglet <strong>"Paiements"</strong> après création de la réservation.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <Label className="font-medium">Taxe de séjour</Label>
                <p className="text-xs text-muted-foreground">Assujetti aux taxes de séjour</p>
              </div>
              <Switch
                checked={formData.tourist_tax_applicable !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, tourist_tax_applicable: checked })}
              />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
        </form>
        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button type="submit" form="reservation-form">
            {selectedReservation ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
